import { Alert, AlertTitle, Link } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { capitalize, flatten, sortBy } from "lodash-es";
import React from "react";
import {
  PimProductIdentifier,
  PimSupplierProductStatusValue,
} from "../../../../clients/graphqlTypes.js";
import { Stack } from "../../../commons/layout/Stack.js";
import { useSnackbar } from "notistack";

export function useAvailabilityWarning() {
  const { t } = useTranslate(["Product", "SupplierProductStatus"]);

  const { enqueueSnackbar } = useSnackbar();

  return React.useCallback(
    (status: PimSupplierProductStatusValue) => {
      enqueueSnackbar(
        t("Product availability to be checked on ordering", {
          ns: "Product",
          status: capitalize(t(status, { ns: "SupplierProductStatus" })),
        }),
        { variant: "warning" }
      );
    },
    [enqueueSnackbar, t]
  );
}

type Props = {
  items: {
    productIdentifier: PimProductIdentifier;
    productStatus?: { value: PimSupplierProductStatusValue } | null;
    items: Array<{
      id: string;
      title: string;
      pathForPdf: string;
      pathSortable: string;
    }>;
  }[];
  navigateToItem: (id: string) => void;
};

export function QuoteProductsAvailabilityAlert({
  items,
  navigateToItem,
}: Props) {
  const { t } = useTranslate(["QuoteEdit", "SupplierProductStatus"]);

  const itemsWithStatus = React.useMemo(
    () =>
      sortBy(
        flatten(
          items.map(({ items, productStatus }) =>
            items.map(item => ({ productStatus, item }))
          )
        ),
        e => e.item.pathSortable
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items.flatMap(i => i.items).join(":")]
  );

  return (
    <Alert variant="outlined" severity="warning">
      <AlertTitle>
        {t("Products availability to be checked on ordering")}
      </AlertTitle>
      <Stack flexDirection={"column"}>
        {itemsWithStatus.map(({ productStatus, item }) => (
          <div key={item.id}>
            <Link
              onClick={() => navigateToItem(item.id)}
              color="secondary"
              sx={{ cursor: "pointer" }}
            >
              {`${item.pathForPdf} ${item.title}`}
            </Link>
            {productStatus
              ? ` ${t(productStatus.value, { ns: "SupplierProductStatus" })}`
              : ""}
          </div>
        ))}
      </Stack>
    </Alert>
  );
}
